import {
  Alert,
  LinkButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography,
} from '@compass/components'
import { cn } from '@compass/utils'
import { useRouter } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { GenericError } from 'const'
import { GenericEmptyState } from 'features/commonUI'
import { formatNum } from 'helpers/format'
import { Grid2X2, SearchIcon } from 'lucide-react'

import {
  BOT_IDENTITY_COLORS,
  BOT_IDENTITY_ICONS,
  getBotCategoryIcon,
  getBotCategoryLabel,
  getBotIdentityLabel,
} from '../consts'
import {
  BotDetectionPredefinedDateRangeId,
  isEdgePredefinedDateRangeId,
  isIdentificationPredefinedDateRangeId,
} from '../dateRanges'
import { BotCategory, BotDetectionCategoryIdentityMatrix, BotDetectionSource, BotIdentity } from '../models'
import { BotDetectionOverviewFilterValues } from './useBotDetectionOverviewFilter'

const IDENTITY_COLUMNS: BotIdentity[] = ['verified', 'signed', 'unknown', 'spoofed']
const MATRIX_COLUMN_COUNT = IDENTITY_COLUMNS.length + 1

/** A cell is filled solid once it reaches this share of the matrix's largest cell. */
const MATRIX_FILL_THRESHOLD = 0.2

type MatrixCellAppearance = 'filled' | 'accent' | 'muted'

/**
 * Decides how a matrix cell is rendered, based on its value and the largest value in the matrix.
 * - `muted`  — zero (or negative) value, gray text.
 * - `filled` — value is significant (≥ 20% of the largest cell): solid identity color, white text.
 * - `accent` — non-zero but below the threshold: identity-colored text, no fill.
 */
function getMatrixCellAppearance(value: number, matrixMax: number): MatrixCellAppearance {
  if (value <= 0) {
    return 'muted'
  }
  if (matrixMax > 0 && value >= MATRIX_FILL_THRESHOLD * matrixMax) {
    return 'filled'
  }
  return 'accent'
}

const getMatrixMax = (matrix: BotDetectionCategoryIdentityMatrix) =>
  Math.max(0, ...matrix.flatMap((row) => IDENTITY_COLUMNS.map((identity) => row[identity])))

function CategoryLabel({ category }: { category: BotDetectionCategoryIdentityMatrix[number]['category'] }) {
  const Icon = getBotCategoryIcon(category)

  return (
    <div className='flex min-w-0 items-center gap-2'>
      {Icon ? <Icon size={16} className='shrink-0 text-gray-700' /> : null}
      <Typography weight='medium' className='truncate'>
        {getBotCategoryLabel(category)}
      </Typography>
    </div>
  )
}

function MatrixValueCell({
  value,
  identity,
  category,
  matrixMax,
  isLastColumn,
  subscriptionId,
  since,
  before,
  period,
  filterValues,
  source,
}: {
  value: number
  identity: BotIdentity
  category: BotCategory
  matrixMax: number
  isLastColumn: boolean
  subscriptionId: string
  since?: string
  before?: string
  period?: BotDetectionPredefinedDateRangeId
  filterValues?: Partial<BotDetectionOverviewFilterValues>
  source?: BotDetectionSource
}) {
  const router = useRouter()
  const appearance = getMatrixCellAppearance(value, matrixMax)
  const formattedValue = formatNum(value)
  const { environmentIds, ...botTaxonomyFilters } = filterValues ?? {}

  return (
    <TableCell
      bleed
      className={cn(
        'relative text-center font-mono',
        !isLastColumn && 'border-r border-gray-300',
        appearance === 'filled' && BOT_IDENTITY_COLORS[identity].cellBg
      )}
    >
      {({ isHovered }) => (
        <div className='relative flex items-center justify-center px-4 py-3.5'>
          <span
            className={cn(
              appearance === 'filled' && 'text-white',
              appearance === 'accent' && BOT_IDENTITY_COLORS[identity].text,
              appearance === 'muted' && 'text-gray-600'
            )}
          >
            {formattedValue}
          </span>
          {value > 0 && (
            <LinkButton
              href={
                router.buildLocation({
                  to: source === 'identification' ? AppRoute.IdentificationEvents : AppRoute.BotDetectionEvents,
                  params: { subscriptionId },
                  search:
                    source === 'identification'
                      ? {
                          ...(isIdentificationPredefinedDateRangeId(period) ? { period } : { since, before }),
                          ...botTaxonomyFilters,
                          ...(environmentIds ? { environment: environmentIds } : {}),
                          bot:
                            identity === 'spoofed'
                              ? 'bad'
                              : identity === 'verified' || identity === 'signed'
                                ? 'good'
                                : 'all',
                          category: [category],
                          identity: [identity],
                        }
                      : {
                          ...(isEdgePredefinedDateRangeId(period)
                            ? { period }
                            : { since, before, aggregateRange: true }),
                          ...filterValues,
                          source: 'edge',
                          category: [category],
                          identity: [identity],
                        },
                }).href
              }
              variant='secondary'
              size='sm'
              isIcon
              aria-label={source === 'identification' ? 'Open in identification events' : 'Open in events'}
              // Prevent tooltip from persisting when tooltip is hovered
              tooltipProps={{
                className: 'pointer-events-none',
              }}
              className={({ isFocusVisible }) =>
                cn(
                  'absolute top-1/2 right-2.5 z-10 -translate-y-1/2',
                  appearance === 'filled' && 'border-none outline-solid outline-1 outline-black/5',
                  isHovered || isFocusVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
                )
              }
            >
              <SearchIcon />
            </LinkButton>
          )}
        </div>
      )}
    </TableCell>
  )
}

export function BotDetectionIdentityMatrix({
  matrix,
  isLoading,
  error,
  subscriptionId,
  since,
  before,
  period,
  filterValues,
  source,
}: {
  matrix?: BotDetectionCategoryIdentityMatrix
  isLoading: boolean
  error?: GenericError | null
  subscriptionId: string
  since?: string
  before?: string
  period?: BotDetectionPredefinedDateRangeId
  filterValues?: Partial<BotDetectionOverviewFilterValues>
  source?: BotDetectionSource
}) {
  const matrixMax = matrix ? getMatrixMax(matrix) : 0
  const errorMessage = error?.message ?? 'Something went wrong. Please try again.'
  const hasMatrixRows = Boolean(matrix?.length)

  const statusContent = isLoading ? (
    <Skeleton data-testid='table-loading-skeleton' className='h-5 w-full transform-none' />
  ) : error ? (
    <Alert variant='error'>{errorMessage}</Alert>
  ) : !hasMatrixRows ? (
    <GenericEmptyState
      icon={<Grid2X2 />}
      title='No identity breakdown'
      description='Try adjusting the filters or date range.'
    />
  ) : null

  return (
    <section className='flex flex-col gap-4' aria-label='Category identity matrix'>
      <Paper className='overflow-x-auto overscroll-x-none p-0' padded={false}>
        <Table aria-label='Category identity matrix table' size='lg' className='w-full min-w-200 table-fixed'>
          <TableHeader className='border-b border-gray-300'>
            <TableColumn id='category' isRowHeader className='border-r border-gray-300 px-4! py-3.5!'>
              Identity breakdown
            </TableColumn>
            {IDENTITY_COLUMNS.map((identity, index) => {
              const Icon = BOT_IDENTITY_ICONS[identity]

              return (
                <TableColumn
                  key={identity}
                  id={identity}
                  className={cn(
                    'px-4! py-3.5! [&_div]:justify-center',
                    index < IDENTITY_COLUMNS.length - 1 && 'border-r border-gray-300'
                  )}
                  contentClassName={BOT_IDENTITY_COLORS[identity].text}
                >
                  <span className='flex items-center justify-center gap-2'>
                    <Icon size={16} className={cn('shrink-0', BOT_IDENTITY_COLORS[identity].text)} />
                    {getBotIdentityLabel(identity)}
                  </span>
                </TableColumn>
              )
            })}
          </TableHeader>
          <TableBody>
            {statusContent ? (
              <TableRow>
                <TableCell colSpan={MATRIX_COLUMN_COUNT} className={cn(error || isLoading ? 'p-4!' : 'p-0!')}>
                  {statusContent}
                </TableCell>
              </TableRow>
            ) : (
              matrix?.map((row) => (
                <TableRow key={row.category} id={`category-${row.category}`}>
                  <TableCell className='border-r border-gray-300 px-4! py-3.5!'>
                    <CategoryLabel category={row.category} />
                  </TableCell>
                  {IDENTITY_COLUMNS.map((identity, index) => (
                    <MatrixValueCell
                      key={`${row.category}-${identity}`}
                      value={row[identity]}
                      identity={identity}
                      category={row.category}
                      matrixMax={matrixMax}
                      isLastColumn={index === IDENTITY_COLUMNS.length - 1}
                      subscriptionId={subscriptionId}
                      since={since}
                      before={before}
                      period={period}
                      filterValues={filterValues}
                      source={source}
                    />
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </section>
  )
}
